// SpriteAtlas — loads a TexturePacker sprite sheet (hash format)
// and draws individual sprites by key, handling trimmed + rotated frames.

export class SpriteAtlas {
  /**
   * @param {HTMLImageElement} image
   * @param {object} json - parsed TexturePacker JSON (hash format)
   */
  constructor(image, json) {
    this.image = image;
    this.frames = json.frames;
    this._keys = Object.keys(this.frames);
  }

  /**
   * Load atlas image and combine with already-parsed JSON data.
   * @param {string} imageSrc - URL/path to the .png
   * @param {object} json - already-parsed TexturePacker JSON
   * @returns {Promise<SpriteAtlas>}
   */
  static async load(imageSrc, json) {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageSrc;
    });
    return new SpriteAtlas(image, json);
  }

  /** @param {string} key */
  has(key) {
    return key in this.frames;
  }

  /** @returns {string[]} */
  keys() {
    return this._keys;
  }

  /**
   * Return all keys that start with the given prefix.
   * @param {string} prefix
   * @returns {string[]}
   */
  keysWithPrefix(prefix) {
    return this._keys.filter(k => k.startsWith(prefix));
  }

  /**
   * Draw a sprite at (dx, dy) — top-left of the logical sourceSize box.
   * Handles TexturePacker `rotated` and `trimmed` flags.
   *
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} key
   * @param {number} dx - destination x (top-left of logical frame)
   * @param {number} dy - destination y (top-left of logical frame)
   */
  draw(ctx, key, dx, dy) {
    const entry = this.frames[key];
    if (!entry) return;

    const { frame, rotated, trimmed, spriteSourceSize } = entry;
    const ox = trimmed ? spriteSourceSize.x : 0;
    const oy = trimmed ? spriteSourceSize.y : 0;

    ctx.imageSmoothingEnabled = false;

    if (rotated) {
      // TexturePacker rotates the sprite 90° CW in the sheet.
      // frame.w/h are the ORIGINAL (un-rotated) dimensions, so the atlas
      // rect is actually frame.h wide × frame.w tall (swapped).
      // To undo: rotate 90° CCW (-π/2) and adjust translation.
      ctx.save();
      ctx.translate(dx + ox, dy + oy + frame.h);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(
        this.image,
        frame.x, frame.y, frame.h, frame.w,
        0, 0, frame.h, frame.w,
      );
      ctx.restore();
    } else {
      ctx.drawImage(
        this.image,
        frame.x, frame.y, frame.w, frame.h,
        dx + ox, dy + oy, frame.w, frame.h,
      );
    }
  }
}
